/**
 * Presentation-layer shaper for translated karaoke clips.
 *
 * The data layer carries phrase-level translated units (`{ text, ts,
 * endTs }`), one per `TranslationPhrase`. The renderer's transcript
 * source needs per-word units so its built-in karaoke highlight can
 * advance word-by-word over the translation, matching the English
 * path. We synthesize per-word timing by splitting each phrase's
 * window over the words in its translated text.
 *
 * Two split strategies are supported:
 *  - `'character-weighted'` (default): each word's slice of the phrase
 *    duration is proportional to its character count. Longer words
 *    pulse longer, which approximates real speech pacing without
 *    needing any per-word timing data.
 *  - `'equal-weighted'`: each word gets `phraseDuration / wordCount`.
 *    Preserved as an opt-in for callers that want uniform pacing or
 *    visual-regression comparisons against the prior shipped behavior.
 *
 * Word tokenization and per-word duration math are shared with
 * `syntheticCaptionBreaks.ts` (PLAT-9623) via `karaokeWordTiming.ts`,
 * so the highlight cursor and synthetic-break placement stay in lockstep.
 *
 * This is purely a visual shaping concern — translation data and
 * timing on the domain model stay phrase-level (FM-1 invariant).
 */

import { containsWideChar, visualTextWidth } from '@src/utility/strings';

import { endLineAtUnit, splitUnitsIntoLines } from './captionSoftBreaks';
import {
  computeWordDurations,
  DEFAULT_SPLIT_STRATEGY,
  type SplitStrategy,
  tokenizePhraseWords,
} from './karaokeWordTiming';

import type { PhraseSoftBreak } from './captionSoftBreaks';

export type { SplitStrategy } from './karaokeWordTiming';

export type TranslatedPhraseUnit = {
  text: string;
  ts: number;
  endTs: number;
};

export type AnimationSourceUnit = {
  value: string;
  time: number;
  duration: number;
};

export type ScaleClipToRendererArgs = {
  phrases: readonly TranslatedPhraseUnit[];
  clipStart: number;
  clipEnd: number;
  splitStrategy?: SplitStrategy;
  /**
   * PLAT-11914 — user soft line breaks. The unit before each break ends in
   * `\n`; a break inside a unit (a Japanese character slot the word
   * tokenizer joined) splits that unit, its duration shared by length.
   */
  softBreaks?: readonly PhraseSoftBreak[];
};

/**
 * Split a translated phrase set into per-word renderer units with
 * in-phrase timing scaled into clip-relative time.
 *
 * Returns an empty array when:
 *  - `phrases` is empty
 *  - `clipEnd <= clipStart` (zero or negative target duration)
 *  - every phrase has zero or negative duration AND no words
 *
 * Whitespace inside phrase text is attached to the preceding word
 * unit so spacing on the canvas matches the English per-word path,
 * where punctuation/whitespace is merged into the previous token.
 * Leading whitespace on the first word of the first phrase is
 * preserved as a standalone unit so the renderer doesn't drop it.
 */
export function buildTranslatedAnimationSource({
  phrases,
  clipStart,
  clipEnd,
  splitStrategy = DEFAULT_SPLIT_STRATEGY,
  softBreaks = [],
}: ScaleClipToRendererArgs): AnimationSourceUnit[] {
  if (phrases.length === 0) return [];
  const targetDuration = Math.max(0, clipEnd - clipStart);
  const originalStart = phrases[0].ts;
  const originalEnd = phrases[phrases.length - 1].endTs;
  const originalDuration = originalEnd - originalStart;
  const scale = originalDuration > 0 ? targetDuration / originalDuration : 1;

  const result: AnimationSourceUnit[] = [];
  for (let phraseIndex = 0; phraseIndex < phrases.length; phraseIndex++) {
    const phrase = phrases[phraseIndex];
    const breakOffsets = softBreaks
      .filter(b => b.phraseIndex === phraseIndex)
      .map(b => b.offset)
      .sort((a, b) => a - b);
    let nextBreak = 0;
    let inPhrase = 0;
    const phraseStart = (phrase.ts - originalStart) * scale;
    const phraseDuration = Math.max(0, (phrase.endTs - phrase.ts) * scale);
    const { pieces, words } = tokenizePhraseWords(phrase.text);
    if (words.length === 0) continue;
    const wordDurations = computeWordDurations(
      words,
      phraseDuration,
      splitStrategy,
    );
    let cursor = phraseStart;
    let wordIdx = 0;
    for (const piece of pieces) {
      const pieceStart = inPhrase;
      inPhrase += piece.length;
      if (piece.trim().length === 0) {
        // Whitespace: append to previous word unit. If no previous
        // unit exists (leading whitespace on the first phrase's first
        // word), emit it as its own unit so the renderer preserves it
        // — dropping leading whitespace silently caused alignment
        // drift in earlier iterations of this code.
        if (result.length > 0) {
          result[result.length - 1].value += piece;
        } else {
          result.push({ value: piece, time: cursor, duration: 0 });
        }
        continue;
      }
      const dur = wordDurations[wordIdx];
      let breakBefore = false;
      while (
        nextBreak < breakOffsets.length &&
        breakOffsets[nextBreak] <= pieceStart
      ) {
        breakBefore = true;
        nextBreak += 1;
      }
      const cuts = [0];
      while (
        nextBreak < breakOffsets.length &&
        breakOffsets[nextBreak] < pieceStart + piece.length
      ) {
        cuts.push(breakOffsets[nextBreak] - pieceStart);
        nextBreak += 1;
      }
      cuts.push(piece.length);
      for (let c = 0; c < cuts.length - 1; c++) {
        const value = piece.slice(cuts[c], cuts[c + 1]);
        const share = (dur * value.length) / piece.length;
        result.push({ value, time: cursor, duration: share });
        cursor += share;
        if (c > 0 || breakBefore) {
          endLineAtUnit(result, result.length - 2);
        }
      }
      wordIdx += 1;
    }
  }
  return result;
}

/**
 * Hard line breaks for a full-width caption the canvas cannot wrap itself:
 * Creatomate wraps only at spaces, so spaceless Japanese/Chinese runs off
 * the frame on one line. Appends `\n` to the unit that ends each line, at
 * word-unit boundaries, splitting the row into the fewest lines of at most
 * `maxLineWidth` (visual width), balanced so no line is a short stub.
 * Latin rows and rows that already fit come back unchanged.
 */
export function wrapWideAnimationUnits(
  units: readonly AnimationSourceUnit[],
  maxLineWidth: number,
): AnimationSourceUnit[] {
  const text = units.map(unit => unit.value).join('');
  const totalWidth = visualTextWidth(text);
  if (!containsWideChar(text) || totalWidth <= maxLineWidth) {
    return [...units];
  }
  const lineCount = Math.ceil(totalWidth / maxLineWidth);
  const targetWidth = totalWidth / lineCount;
  const wrapped = units.map(unit => ({ ...unit }));
  let currentWidth = 0;
  let linesStarted = 1;
  for (let i = 0; i < wrapped.length; i++) {
    const width = visualTextWidth(wrapped[i].value);
    // Break where the line lands closest to an even split (without ever
    // exceeding the max); past the planned line count, only on overflow.
    const overflows = currentWidth + width > maxLineWidth;
    const closerBefore =
      linesStarted < lineCount &&
      Math.abs(currentWidth - targetWidth) <=
        Math.abs(currentWidth + width - targetWidth);
    if (currentWidth > 0 && (overflows || closerBefore)) {
      wrapped[i - 1].value = `${wrapped[i - 1].value.trimEnd()}\n`;
      currentWidth = 0;
      linesStarted += 1;
    }
    currentWidth += width;
  }
  return wrapped;
}

/**
 * PLAT-11914 — `wrapWideAnimationUnits` for a caption that already has user
 * soft breaks: each user line is kept and wrapped on its own, so a user
 * break is never undone and a user line that still overflows the box still
 * wraps.
 */
export function wrapWideAnimationLines(
  units: readonly AnimationSourceUnit[],
  maxLineWidth: number,
): AnimationSourceUnit[] {
  return splitUnitsIntoLines(units).flatMap(line =>
    wrapWideAnimationUnits(line, maxLineWidth),
  );
}

/**
 * A caption's `text` with a `\n` wherever one of its animation units ends a
 * line (user soft breaks, Punctuation-style wrapping).
 *
 * Joining the units' values instead loses what the units don't carry: the
 * space between translated phrases, whitespace the transcript never stored as
 * tokens, and words with no timing. So each unit is found in `text`, in order,
 * and the text between units is kept as-is; a line-ending unit drops the
 * whitespace after it for the `\n`. Falls back to the joined values if a unit
 * can't be found.
 */
export function insertUnitLineEnds(
  text: string,
  units: readonly AnimationSourceUnit[],
): string {
  let out = '';
  let cursor = 0;
  for (const unit of units) {
    const endsLine = unit.value.endsWith('\n');
    const value = unit.value.trim();
    if (value.length > 0) {
      const at = text.indexOf(value, cursor);
      if (at < 0) return units.map(u => u.value).join('');
      out += text.slice(cursor, at + value.length);
      cursor = at + value.length;
    }
    if (endsLine) {
      out = `${out.trimEnd()}\n`;
      while (cursor < text.length && /\s/.test(text[cursor])) cursor += 1;
    }
  }
  return out + text.slice(cursor);
}
